import {
  type AskPunttProjectFile,
  type AskPunttProjectScratchpadEntry,
} from "@mg/schemas/src/christo/askPunttProject";
import {
  CaretDown,
  CircleNotch,
  FileText,
  ImageSquare,
  PencilSimple,
  VideoCamera,
} from "@phosphor-icons/react";
import cx from "classnames";
import { useState } from "react";

import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";
import { downloadAsset } from "../../../utils/http";
import {
  displayFileNameFromSource,
  type ProjectReferenceDocumentRow,
} from "../utils/projectContextDocuments";

/**
 * Right-hand "PROJECT CONTEXT" panel for the project workspace. Renders
 * user-uploaded, versioned project files. Owners get a pencil affordance to
 * edit the project's description / metadata.
 */
function fileTypeIcon(type: string) {
  if (type === "image") return ImageSquare;
  if (type === "video") return VideoCamera;
  return FileText;
}

function fileTypeLabel(type: string, name: string): string {
  const ext = name.split(".").pop()?.toUpperCase();
  if (ext && ext.length <= 5 && ext !== name.toUpperCase()) return ext;
  if (type === "image") return "IMAGE";
  if (type === "video") return "VIDEO";
  return "PDF";
}

export function CampaignDocsPanel({
  documents,
  description,
  upcomingWork,
  details = [],
  onEdit,
  panelOpen: panelOpenProp,
  onPanelOpenChange,
}: {
  /** One row per file version (newest first). */
  documents: ProjectReferenceDocumentRow[];
  /** The project's overall description, surfaced above the documents. */
  description?: string;
  /** Agent-maintained summary of next steps for this project. */
  upcomingWork?: string;
  /**
   * Surfaced agent scratch-pad entries (the agent flagged these for display).
   * Read-only — the agent maintains them; users cannot edit them here.
   */
  details?: AskPunttProjectScratchpadEntry[];
  /** Owner-only edit affordance for the project description / metadata. */
  onEdit?: () => void;
  /** Controlled expand state for the project context section header. */
  panelOpen?: boolean;
  onPanelOpenChange?: (open: boolean) => void;
}) {
  const [panelOpenInternal, setPanelOpenInternal] = useState(true);
  const panelOpen = panelOpenProp ?? panelOpenInternal;
  const setPanelOpen = (next: boolean) => {
    onPanelOpenChange?.(next);
    if (panelOpenProp === undefined) setPanelOpenInternal(next);
  };
  const [docsOpen, setDocsOpen] = useState(true);
  const upcomingWorkText = upcomingWork?.trim() ?? "";

  return (
    <section
      className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white ${panelOpen ? "h-full flex-1" : "shrink-0"}`}
      aria-label="Project context"
      data-testid="project-context-panel"
      data-panel-expanded={panelOpen ? "true" : "false"}
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-puntt-neutral-gray-5 px-4 py-3">
        <button
          type="button"
          onClick={() => setPanelOpen(!panelOpen)}
          aria-expanded={panelOpen}
          aria-label="Toggle project context section"
          data-testid="project-context-toggle"
          className="flex min-w-0 flex-1 items-center gap-2 bg-base-white text-left"
        >
          <h2 className="flex-1 text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-12">
            Project context
          </h2>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${panelOpen ? "" : "-rotate-90"}`}
          />
        </button>
      </div>

      {panelOpen && (
        <div
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain p-4 [scrollbar-width:thin]"
          data-testid="project-context-scroll"
        >
          {(description || onEdit) && (
            <div data-testid="campaign-project-description">
              <div
                className="flex items-center gap-2"
                data-testid="campaign-project-description-header"
              >
                <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                  Instructions
                </h3>
                {onEdit && (
                  <button
                    type="button"
                    onClick={onEdit}
                    aria-label="Edit project context"
                    data-testid="project-context-edit-button"
                    className="flex size-7 shrink-0 items-center justify-center rounded-md bg-transparent text-puntt-neutral-gray-11 transition-colors hover:text-puntt-neutral-gray-12"
                  >
                    <PencilSimple size={15} />
                  </button>
                )}
              </div>
              {description ? (
                <p className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {description}
                </p>
              ) : null}
            </div>
          )}

          <div data-testid="campaign-project-upcoming-work">
            <h3 className="text-[13px] font-bold text-puntt-neutral-gray-12">
              Next steps
            </h3>
            <p
              className="mt-0.5 text-[12px] leading-relaxed text-puntt-neutral-gray-10"
              data-testid="campaign-project-upcoming-work-help"
            >
              Planned work and deadlines for this project.
            </p>
            {upcomingWorkText ? (
              <p className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                {upcomingWorkText}
              </p>
            ) : (
              <p
                className="mt-1.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                data-testid="campaign-project-upcoming-work-empty"
              >
                No next steps noted yet.
              </p>
            )}
          </div>

          {details.length > 0 && (
            <div data-testid="campaign-project-details">
              <h3 className="text-[13px] font-bold text-puntt-neutral-gray-12">
                Project details
              </h3>
              <dl className="mt-1.5 flex flex-col gap-2">
                {details.map((entry) => (
                  <div
                    key={entry.key}
                    data-testid={`campaign-project-detail-${entry.key}`}
                  >
                    <dt className="text-[12px] font-semibold text-puntt-neutral-gray-12">
                      {entry.label?.trim() || entry.key}
                    </dt>
                    <dd className="mt-0.5 text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                      {entry.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={() => setDocsOpen((o) => !o)}
              aria-expanded={docsOpen}
              aria-label="Toggle reference documents"
              data-testid="campaign-docs-toggle"
              className="flex w-full items-center gap-2 rounded-md bg-base-white p-2 text-left"
            >
              <h3 className="flex-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                Reference Documents
              </h3>
              <CaretDown
                size={14}
                aria-hidden="true"
                className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${docsOpen ? "" : "-rotate-90"}`}
              />
            </button>

            {docsOpen && (
              <div
                className="mt-2 flex flex-col gap-2"
                data-testid="campaign-docs-body"
              >
                {documents.length === 0 ? (
                  <p
                    className="text-[13px] leading-relaxed text-puntt-neutral-gray-11"
                    data-testid="campaign-docs-empty"
                  >
                    No documents yet. Files you upload to this project show up
                    here.
                  </p>
                ) : (
                  documents.map((row) => (
                    <ReferenceFileRow
                      key={`${row.file.fileId}-v${row.versionOrdinal}`}
                      row={row}
                      showVersionBadge={(row.file.versions?.length ?? 0) > 1}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function referenceRowDisplayName(
  file: AskPunttProjectFile,
  version: AskPunttProjectFile["versions"][number],
): string {
  const trimmed = file.name?.trim();
  if (trimmed && trimmed !== "File") return trimmed;
  return (
    displayFileNameFromSource(version.name) ??
    displayFileNameFromSource(version.source) ??
    displayFileNameFromSource(version.key) ??
    trimmed ??
    "File"
  );
}

function ReferenceFileRow({
  row,
  showVersionBadge,
}: {
  row: ProjectReferenceDocumentRow;
  showVersionBadge: boolean;
}) {
  const { file, version, versionOrdinal } = row;
  const resolvedSource = version.source ?? version.key ?? "";
  const resolvedName = referenceRowDisplayName(file, version);
  const resolvedType = file.type ?? "file";
  const testId = `campaign-file-download-${file.fileId}-v${versionOrdinal}`;

  return (
    <DownloadableReferenceRow
      source={resolvedSource}
      name={resolvedName}
      type={resolvedType}
      testId={testId}
      versionOrdinal={showVersionBadge ? versionOrdinal : undefined}
    />
  );
}

function DownloadableReferenceRow({
  source,
  name,
  type,
  testId,
  versionOrdinal,
}: {
  source: string;
  name: string;
  type: string;
  testId: string;
  versionOrdinal?: number;
}) {
  const [downloading, setDownloading] = useState(false);
  const TypeIcon = fileTypeIcon(type);
  const typeLabel = fileTypeLabel(type, name);

  const handleDownload = async () => {
    if (downloading || !source) return;
    setDownloading(true);
    try {
      await downloadAsset(source, name);
    } catch (err) {
      console.error("Failed to download project context file", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading || !source}
      data-testid={testId}
      aria-label={
        versionOrdinal != null
          ? `Download ${name} version ${versionOrdinal}`
          : `Download ${name}`
      }
      className={cx(
        "flex w-full items-center gap-2 px-2 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-60",
        interactiveSurfaceClasses({
          interactive: true,
          disabled: downloading || !source,
        }),
      )}
    >
      <span
        aria-hidden="true"
        className={interactiveSurfaceIconClasses(
          "flex size-5 shrink-0 items-center justify-center text-puntt-neutral-gray-11",
        )}
      >
        {downloading ? (
          <CircleNotch size={18} className="animate-spin" />
        ) : (
          <TypeIcon size={18} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
        {name}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {versionOrdinal != null && (
          <span
            className="font-mono rounded border border-puntt-neutral-gray-12 bg-puntt-neutral-gray-12 px-1.5 py-px text-[10px] font-medium text-base-white"
            data-testid={`${testId}-version`}
          >
            v{versionOrdinal}
          </span>
        )}
        <span className="text-[10px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
          {typeLabel}
        </span>
      </span>
    </button>
  );
}
